
import React, { useCallback, useEffect, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export interface PlacePickerBatchListSectionProps {
    /** When list length or identity changes, scroll hint recalculates */
    scrollKey: string | number;
    list: React.ReactNode;
    footer: React.ReactNode;
    hint?: React.ReactNode;
}

/**
 * Scrollable batch place list with a bottom gradient hint when more rows exist below
 * the fold, and a footer pinned outside the scroll area (Add to list always visible).
 */
const PlacePickerBatchListSection: React.FC<PlacePickerBatchListSectionProps> = ({
    scrollKey,
    list,
    footer,
    hint,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [moreBelow, setMoreBelow] = useState(false);

    const updateMoreBelow = useCallback((): void => {
        const root = scrollRef.current;
        const sent = sentinelRef.current;
        if (!root || !sent) return;
        const hasOverflow = root.scrollHeight > root.clientHeight + 2;
        if (!hasOverflow) {
            setMoreBelow(false);
            return;
        }
        const rootRect = root.getBoundingClientRect();
        const sentRect = sent.getBoundingClientRect();
        const sentinelVisible =
            sentRect.bottom > rootRect.top && sentRect.top < rootRect.bottom;
        setMoreBelow(!sentinelVisible);
    }, []);

    useEffect(() => {
        const root = scrollRef.current;
        if (!root) return;

        updateMoreBelow();
        const raf = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(updateMoreBelow);
        });

        const ro = new ResizeObserver(() => {
            updateMoreBelow();
        });
        ro.observe(root);

        root.addEventListener('scroll', updateMoreBelow, { passive: true });

        return () => {
            window.cancelAnimationFrame(raf);
            ro.disconnect();
            root.removeEventListener('scroll', updateMoreBelow);
        };
    }, [scrollKey, updateMoreBelow]);

    return (
        <div className="place-picker-batch-list-section">
            {hint}
            <div ref={scrollRef} className="place-picker-batch-list-scroll">
                {list}
                <div
                    className={
                        moreBelow
                            ? 'place-picker-batch-scroll-fade place-picker-batch-scroll-fade--visible'
                            : 'place-picker-batch-scroll-fade'
                    }
                    aria-hidden
                >
                    <KeyboardArrowDownIcon
                        className="place-picker-batch-scroll-fade-icon"
                        sx={{ fontSize: 18, color: '#6e6e73' }}
                    />
                    <span className="place-picker-batch-scroll-fade-text">Scroll for more</span>
                </div>
                <div ref={sentinelRef} className="place-picker-batch-scroll-sentinel" aria-hidden />
            </div>
            <div className="place-picker-batch-list-footer">{footer}</div>
        </div>
    );
};

export default React.memo(PlacePickerBatchListSection);
